interface ApiErrorPayload {
  detail?: string;
}

export interface BotStatusPayload {
  run_id: string;
  status: string;
  symbol: string | null;
  heartbeat_at: string;
  updated_at: string;
  payload: Record<string, unknown>;
}

export interface BotTradeEventMetadata {
  event_type?:
    | 'entry'
    | 'exit'
    | 'stop_loss'
    | 'trailing_stop'
    | 'pending_entry'
    | 'stop_update'
    | 'trail_update'
    | 'breakeven_update';
  mode?: 'paper' | 'live';
  strategy?: string;
  [key: string]: unknown;
}

export interface BotTradeEventPayload {
  event_id: string;
  run_id: string;
  symbol: string;
  side: string | null;
  quantity: number | null;
  price: number | null;
  strategy?: string | null;
  occurred_at: string;
  ingested_at: string;
  payload: BotTradeEventMetadata;
}

export interface BotTradesQueryParams {
  symbol?: string;
  runId?: string;
  strategy?: string;
  limit?: number;
  since?: string;
}

export interface ClearBotTradesParams {
  symbol: string;
  runId?: string;
}

export interface ClearBotTradesResponse {
  ok: boolean;
  deleted: number;
  symbol: string;
  runId: string | null;
}

export interface ResetBotSessionParams {
  symbol: string;
}

export interface ResetBotSessionResponse {
  ok: boolean;
  symbol: string;
  deletedTrades: number;
  deletedStatuses: number;
  deletedStateFiles: number;
}

async function parseApiError(response: Response): Promise<Error> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    const payload = (await response.json()) as ApiErrorPayload;
    if (typeof payload.detail === 'string' && payload.detail.length > 0) {
      return new Error(payload.detail);
    }
  }
  return new Error(`Request failed with status ${response.status}`);
}

async function requestJson<T>(input: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(input, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });

  if (!response.ok) {
    throw await parseApiError(response);
  }

  return (await response.json()) as T;
}

export async function fetchBotStatus(): Promise<BotStatusPayload[]> {
  return requestJson<BotStatusPayload[]>('/api/bot/status', {
    method: 'GET',
  });
}

export async function fetchBotTrades(params: BotTradesQueryParams = {}): Promise<BotTradeEventPayload[]> {
  const query = new URLSearchParams();
  if (params.symbol) {
    query.set('symbol', params.symbol);
  }
  if (params.runId) {
    query.set('run_id', params.runId);
  }
  if (params.strategy) {
    query.set('strategy', params.strategy);
  }
  if (typeof params.limit === 'number') {
    query.set('limit', String(params.limit));
  }
  if (params.since) {
    query.set('since', params.since);
  }

  const url = query.size > 0 ? `/api/bot/trades?${query.toString()}` : '/api/bot/trades';
  return requestJson<BotTradeEventPayload[]>(url, {
    method: 'GET',
  });
}

export async function clearBotTrades(params: ClearBotTradesParams): Promise<ClearBotTradesResponse> {
  const query = new URLSearchParams();
  query.set('symbol', params.symbol);
  if (params.runId) {
    query.set('run_id', params.runId);
  }

  return requestJson<ClearBotTradesResponse>(`/api/bot/trades?${query.toString()}`, {
    method: 'DELETE',
  });
}

export async function resetBotSession(params: ResetBotSessionParams): Promise<ResetBotSessionResponse> {
  const query = new URLSearchParams();
  query.set('symbol', params.symbol);

  return requestJson<ResetBotSessionResponse>(`/api/bot/session/reset?${query.toString()}`, {
    method: 'POST',
  });
}
